import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  IconCash,
  IconTrendingUp,
  IconPercentage,
  IconCalendar,
  IconClock,
  IconCheck,
  IconX,
  IconRefresh,
} from "@tabler/icons-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { useAgentCommission, useAgentCommissionSummary } from "@/hooks/use-agent-commissions";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/commission/")({
  component: CommissionPage,
});

function CommissionPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <CommissionContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function CommissionContent() {
  const { data: summary, isLoading: summaryLoading, refetch } = useAgentCommissionSummary();
  const { data: report, isLoading: reportLoading } = useAgentCommission({ limit: 50 });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Commission & Earnings</h1>
          <p className="text-muted-foreground mt-1">
            Track your commission earnings and payment history
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={summaryLoading}
        >
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Commission Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earned</CardTitle>
            <IconCash className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="h-9 w-20 animate-pulse bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  ৳{summary?.all_time?.total_earned?.toLocaleString() || "0"}
                </div>
                <p className="text-xs text-muted-foreground">
                  All time earnings
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <IconCalendar className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="h-9 w-20 animate-pulse bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold text-blue-600">
                  ৳{summary?.this_month?.total_earned?.toLocaleString() || "0"}
                </div>
                <p className="text-xs text-muted-foreground">
                  Earned this month
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commission Rate</CardTitle>
            <IconPercentage className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="h-9 w-20 animate-pulse bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold text-green-600">
                  {(summary?.commission_rate || 0) * 100}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Your current rate
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <IconTrendingUp className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="h-9 w-20 animate-pulse bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold text-orange-600">
                  ৳{summary?.commission_pending.toLocaleString() || "0"}
                </div>
                <p className="text-xs text-muted-foreground">
                  Awaiting payout
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Paid Earnings</CardTitle>
            <CardDescription>Total commission paid to you</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="h-9 w-32 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-3xl font-bold text-green-600">
                ৳{summary?.commission_paid.toLocaleString() || "0"}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Due for Payment</CardTitle>
            <CardDescription>Commission awaiting processing</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="h-9 w-32 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-3xl font-bold text-blue-600">
                ৳{summary?.commission_due.toLocaleString() || "0"}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Commission History */}
      <Card>
        <CardHeader>
          <CardTitle>Commission History</CardTitle>
          <CardDescription>
            Detailed breakdown of your commission earnings per transaction
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reportLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading commission history...</p>
            </div>
          ) : !report?.transactions || report.transactions.length === 0 ? (
            <div className="text-center py-8">
              <IconCash className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No commission transactions yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Start processing transactions to earn commissions
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Transaction</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.transactions.map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell>
                        {formatDistanceToNow(new Date(txn.created_at), {
                          addSuffix: true,
                        })}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {txn.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell>৳{txn.amount.toLocaleString()}</TableCell>
                      <TableCell className="font-medium text-green-600">
                        ৳{txn.commission.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            txn.status === "paid"
                              ? "default"
                              : txn.status === "pending"
                                ? "secondary"
                                : "outline"
                          }
                          className="flex items-center gap-1 w-fit"
                        >
                          {txn.status === "paid" && <IconCheck className="w-3 h-3" />}
                          {txn.status === "pending" && <IconClock className="w-3 h-3" />}
                          {txn.status === "cancelled" && <IconX className="w-3 h-3" />}
                          <span className="capitalize">{txn.status}</span>
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Last Payment Info */}
      {summary?.last_payment && (
        <Card>
          <CardHeader>
            <CardTitle>Last Payment</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Last payment received on{" "}
              {new Date(summary.last_payment).toLocaleDateString()}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
